'use strict';

const libPictView = require('pict-view');

/**
 * Shell — top-level chrome host for plansheet.
 *
 * Replaces the old "Layout owns everything inside #App-Container" pattern.
 * The Shell renders into #App-Container, builds a modal-shell with:
 *   - top    panel: Theme-TopBar (BrandMark + Nav + User slots)
 *   - left   panel: #Plansheet-Sidebar (the docked site-map navigation)
 *   - center: #Plansheet-Workspace, where the Dropzone / Layout
 *             views render (the app swaps between them on session load).
 * Appearance + account settings live on the /settings page, not a gear overlay.
 *
 * The Shell builds its panels once on first render; subsequent renders
 * are no-ops (guarded by _shellPanelsBuilt). The app calls render()
 * exactly once during onAfterInitializeAsync.
 */
class PictViewShell extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._shellPanelsBuilt = false;
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (!this._shellPanelsBuilt)
		{
			this._buildShell();
			this._shellPanelsBuilt = true;
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_buildShell()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpMount = document.getElementById('Plansheet-Shell-Mount');
		if (!tmpModal || typeof tmpModal.shell !== 'function' || !tmpMount)
		{
			this.log.warn('Shell: PictSectionModal or mount not available; shell not built.');
			return;
		}

		this._shell = tmpModal.shell(tmpMount, { PersistenceKey: 'plansheet-shell' });

		this._shell.addPanel({
			Hash: 'topbar', Side: 'top', Mode: 'fixed', Size: 56,
			ContentDestinationId: 'Theme-TopBar', ContentView: 'Theme-TopBar'
		});

		// The shared left rail (nav / notebook / split). The app renders Plansheet-Rail into
		// #Plansheet-Sidebar (the panel's content-inner) and drives its mode; one panel keeps the
		// resize + responsive-drawer + persistence story singular. Wider than the old nav-only rail
		// because it also has to hold the notebook.
		this._shell.addPanel({
			Hash: 'sidebar', Side: 'left', Mode: 'resizable',
			Size: 300, MinSize: 200, MaxSize: 560, Title: 'Navigate',
			ContentDestinationId: 'Plansheet-Sidebar',
			ResponsiveDrawer: 860
		});

		// The contextual help drawer. Hidden (no collapse chrome) so it only opens on demand from F1 or
		// the top-bar "?"; the app drives expand/collapse and refreshes it to the current route. Persist
		// is off so help always starts closed rather than reopening from a prior session. The bound
		// ContentView fills the panel with the drawer view up front; the app calls refreshForRoute on open.
		this._shell.addPanel({
			Hash: 'help', Side: 'right', Mode: 'resizable',
			Size: 380, MinSize: 300, MaxSize: 560, Title: 'Help',
			ContentDestinationId: 'Plansheet-HelpDrawer',
			ContentView: 'Plansheet-HelpDrawer',
			Collapsed: true, Hidden: true, Persist: false,
			// Match the sidebar's responsive breakpoint so both side panels switch to drawer mode at the
			// same width; a mismatch leaves the row half-docked and mislays this panel.
			ResponsiveDrawer: 860
		});

		// The notebook is no longer its own panel -- it lives inside the rail (above) as one of the
		// rail's modes, so #Plansheet-Notebook is a pane the Rail view owns. See PictView-Rail.js.
		this._shell.center({ ContentDestinationId: 'Plansheet-Workspace' });
	}

	// Paint (or clear) the top bar's moodboard backdrop: an inline SVG baked from a plan sheet's crop
	// (pict-section-moodboard bakeCropToSVG), dropped BEHIND the brand / nav / user slots with a theme-tuned scrim
	// so those stay legible. Static -- no flow engine -- and pointer-events:none, so clicks pass through to the
	// chrome. Called on load with the active plan sheet's TopBarBackdropSVG; an empty string clears it. Each plan
	// sheet's bar then reads as visually distinct, which is the whole point (you notice a wrong-plan-sheet at a
	// glance). Direct DOM: this injects a chrome element into the theme's own top bar, outside the render cycle.
	applyTopBarBackdrop(pSVG)
	{
		if (typeof document === 'undefined') { return; }
		let tmpBar = document.getElementById('Theme-TopBar');
		if (!tmpBar) { return; }
		let tmpBackdrop = tmpBar.querySelector('.ps-topbar-backdrop');
		let tmpSVG = String(pSVG || '').trim();
		if (!tmpSVG)
		{
			if (tmpBackdrop) { tmpBackdrop.remove(); }
			tmpBar.classList.remove('ps-topbar-has-backdrop');
			return;
		}
		if (!tmpBackdrop)
		{
			tmpBackdrop = document.createElement('div');
			tmpBackdrop.className = 'ps-topbar-backdrop';
			tmpBar.insertBefore(tmpBackdrop, tmpBar.firstChild);
		}
		tmpBackdrop.innerHTML = tmpSVG;
		let tmpEl = tmpBackdrop.querySelector('svg');
		if (tmpEl)
		{
			tmpEl.setAttribute('width', '100%');
			tmpEl.setAttribute('height', '100%');
			tmpEl.setAttribute('preserveAspectRatio', 'xMidYMid slice');
		}
		tmpBar.classList.add('ps-topbar-has-backdrop');
	}
}

module.exports = PictViewShell;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Shell',
	DefaultRenderable: 'Plansheet-Shell',
	DefaultDestinationAddress: '#App-Container',
	AutoRender:                false,
	CSS: /*css*/`
		html, body { height: 100%; margin: 0; padding: 0; }
		body {
			background:  var(--theme-color-background-primary, #ffffff);
			color:       var(--theme-color-text-primary,       #1d2230);
			font-family: var(--theme-typography-family-sans,
				-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif);
		}
		#App-Container { height: 100%; min-height: 0; overflow: hidden; }
		.pict-modal-shell-host    { height: 100%; }
		.pict-modal-shell         { background: var(--theme-color-background-primary, #ffffff); }
		/* Per-plan-sheet moodboard backdrop, painted BEHIND the theme's brand / nav / user slots by
		   applyTopBarBackdrop. A static inline SVG, so no flow engine; pointer-events pass through. */
		#Theme-TopBar.ps-topbar-has-backdrop { position: relative; }
		#Theme-TopBar .ps-topbar-backdrop { position: absolute; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
		#Theme-TopBar .ps-topbar-backdrop svg { width: 100%; height: 100%; display: block; }
		/* Theme scrim: opaque at the two ends (where the brand + account chips sit) fading to mostly-clear in the
		   middle so the art shows. Uses the bar's own theme color, so it follows light / dark automatically.
		   The end stops are in PIXELS, not percentages, because what they have to cover is a fixed width: the
		   brand mark runs to about 220px and the account chip is right-anchored at a similar size, no matter how
		   wide the window is. With percentage stops the opaque zone shrank with the window and the art rode up
		   over the brand and the sidebar toggles on a narrow one. The clear window stays proportional (it is the
		   part that should breathe), and on a window too narrow to hold both ends the stops collapse in order,
		   leaving the bar opaque -- chrome legible, art hidden, which is the right way for this to fail.
		   The numbers are measured off the bar's own slots: the brand mark plus the two sidebar toggles occupy
		   the left through about 316px, and the launcher / help / account chip occupy the right through about
		   160px, so the opaque ends bracket those and the fades sit just outside them. */
		#Theme-TopBar .ps-topbar-backdrop::after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg,
			var(--theme-color-background-panel, #ffffff) 0px,
			var(--theme-color-background-panel, #ffffff) 325px,
			color-mix(in srgb, var(--theme-color-background-panel, #ffffff) 55%, transparent) 400px,
			color-mix(in srgb, var(--theme-color-background-panel, #ffffff) 14%, transparent) 44%,
			color-mix(in srgb, var(--theme-color-background-panel, #ffffff) 14%, transparent) 56%,
			color-mix(in srgb, var(--theme-color-background-panel, #ffffff) 55%, transparent) calc(100% - 240px),
			var(--theme-color-background-panel, #ffffff) calc(100% - 160px),
			var(--theme-color-background-panel, #ffffff) 100%); }
		/* Lift the theme's own top-bar wrapper above the backdrop + scrim, and drop its opaque bar fill so the art
		   shows through -- the backdrop IS the bar's background now. The brand / nav / account chips keep their own
		   subtle backings (the theme paints those), which is the legibility backstop over a busy crop. */
		#Theme-TopBar.ps-topbar-has-backdrop > *:not(.ps-topbar-backdrop) { position: relative; z-index: 1; background: transparent !important; }
		.pict-modal-shell-panel   { background: var(--theme-color-background-panel,   #ffffff); }
		/* Three distinct regions: navigation, content, and help. The "raised" direction differs by mode,
		   so the rails are tuned per mode rather than reusing one stepping. In DARK a deep ground sits
		   under rails that step lighter (lighter reads as lifted). In LIGHT the warm tones turn dingy as
		   they darken, so the canvas is the brightest paper and the rails stay light and clean. The help
		   drawer carries a faint cool brand wash in both modes, so it is set apart by hue, not a muddy
		   fill or a drop shadow. Light is the default (:root); dark overrides under html.theme-dark. The
		   top bar keeps its own brand chrome. */
		/* Content canvas */
		.pict-modal-shell-center { background: var(--theme-color-background-panel, #fbf8f0); overflow: auto; }
		html.theme-dark .pict-modal-shell-center { background: var(--theme-color-background-primary, #14171c); }
		/* Navigation rail */
		.pict-modal-shell-panel-left { background: var(--theme-color-background-primary, #f5f0e4);
			border-right: 1px solid var(--theme-color-border-default, #cfc6b0); }
		html.theme-dark .pict-modal-shell-panel-left { background: var(--theme-color-background-secondary, #1b1f26); border-right: none; }
		/* Keyboard-first users can hide the docked left navigation (the top-bar show/hide button, Cmd+B / Cmd+\\,
		   or the Appearance toggle set html.ps-nav-off). Hide the whole left side wrapper (panel + resize handle
		   + collapse tab); the center fills the gap. */
		html.ps-nav-off .pict-modal-shell-side-left { display: none !important; }
		/* Keep the top-bar launcher + help button visible in the theme's compact (mobile) mode. The theme
		   hides its whole nav slot there (.pict-theme-topbar-nav display:none !important); this ID override
		   beats that class rule, so the launcher and help stay on the bar next to the burger. */
		#Theme-TopBar-Nav { display: flex !important; align-items: center; }
		/* MoodNote text + placeholder are hardcoded dark in pict-section-moodboard (.mb-note color #3a3320),
		   unlike its theme-aware .mb-text card -- unreadable on the dark canvas. Make the default note text and
		   placeholder follow the theme in dark mode; a note with an explicit text color keeps it (inline wins).
		   Proper fix is upstream: .mb-note should use var(--theme-color-text-primary) the way .mb-text does. */
		html.theme-dark .mb-note { color: var(--theme-color-text-primary, #e6e8ec); }
		html.theme-dark .mb-note:empty::before { color: var(--theme-color-text-secondary, rgba(255,255,255,0.45)); }
		/* ---------- Showcase themes: per-theme face + button signatures ----------
		   The palette comes from each theme's bundle; the face + button treatment ride the
		   html[data-theme=...] marker the theme apply-hook stamps. Buttons take a Win9x "almost square"
		   2px corner across the app; Nostalgia goes all the way to a silver 3D-beveled button. */
		.pict-modal-shell [class*="btn"] { border-radius: 2px; }
		/* Per-theme content face -- overrides the --plansheet-content var the base 'html body' rule reads. */
		html[data-theme="nostalgia"]     { --plansheet-content: Tahoma, 'MS Sans Serif', Geneva, Verdana, sans-serif; }
		html[data-theme="1990s-website"] { --plansheet-content: 'Times New Roman', Times, Georgia, serif; }
		html[data-theme="cyberpunk"]     { --plansheet-content: 'Share Tech Mono', Consolas, 'SFMono-Regular', Menlo, ui-monospace, monospace; }
		html[data-theme="neo-tokyo"]     { --plansheet-content: 'Rajdhani', Eurostile, 'Helvetica Neue', Arial, sans-serif; }
		html[data-theme="london-subway"] { --plansheet-content: 'Gill Sans', 'Gill Sans MT', Johnston, Calibri, sans-serif; }
		html[data-theme="high-contrast"] { --plansheet-content: system-ui, 'Segoe UI', Tahoma, sans-serif; }
		html[data-theme="plain"]         { --plansheet-content: system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
		/* 1990s: period beveled buttons. */
		html[data-theme="1990s-website"] .pict-modal-shell [class*="btn"] { border: 2px outset #f4f4f4; border-radius: 0; box-shadow: none; }
		/* Nostalgia: the Win9x button -- silver face, square, 3D bevel (light + dark edge colours). */
		html[data-theme="nostalgia"] .pict-modal-shell [class*="btn"] { border: 0; border-radius: 2px; color: #000; background: #c0c0c0; box-shadow: inset -1px -1px 0 #000, inset 1px 1px 0 #fff, inset -2px -2px 0 #808080, inset 2px 2px 0 #dfdfdf; }
		html[data-theme="nostalgia"].theme-dark .pict-modal-shell [class*="btn"] { color: #e6e6e6; background: #3a3a3a; box-shadow: inset -1px -1px 0 #000, inset 1px 1px 0 #6e6e6e, inset -2px -2px 0 #202020, inset 2px 2px 0 #5a5a5a; }
		html[data-theme="nostalgia"] .pict-modal-shell [class*="btn"]:active { box-shadow: inset 1px 1px 0 #000, inset -1px -1px 0 #dfdfdf, inset 2px 2px 0 #808080; }
		/* Cyberpunk: a soft neon halo on the primary actions. */
		html[data-theme="cyberpunk"] .pict-modal-shell [class*="btn-primary"], html[data-theme="cyberpunk"] .pict-modal-shell .cst-set-btn { box-shadow: 0 0 10px rgba(255,42,109,0.55); }
		/* Responsive (phone) header: keep the launcher + help + the two stamps; drop the PLANSHEET wordmark and
		   the spelled-out user name, plus the now-dangling inner dividers, so each lockup reads as a clean stamp. */
		@media (max-width: 600px) {
			.pict-theme-brand-mark .pict-theme-brand-mark-name { display: none; }
			.pict-theme-brand-mark-icon { border-right: none; }
			#Theme-TopBar-User .cst-user-chip .cst-user-name { display: none; }
			#Theme-TopBar-User .cst-user-chip .cst-user-avatar { border-left: none; }
		}
		/* Help drawer: a cool brand wash + a clear left edge (no shadow). */
		.pict-modal-shell-panel-right { background: var(--theme-color-background-panel, #fbf8f0);
			border-left: 1px solid var(--theme-color-border-default, #cfc6b0); }
		html.theme-dark .pict-modal-shell-panel-right { background: var(--theme-color-background-tertiary, #232830);
			border-left: 1px solid var(--theme-color-border-strong, #46505c); }
		@supports (background: color-mix(in srgb, red, blue)) {
			.pict-modal-shell-panel-right { background: color-mix(in srgb, var(--theme-color-brand-primary, #1d5fa8) 7%, var(--theme-color-background-panel, #fbf8f0)); }
			html.theme-dark .pict-modal-shell-panel-right { background: color-mix(in srgb, var(--theme-color-brand-primary, #4f95e8) 8%, var(--theme-color-background-tertiary, #232830)); }
		}
		#Plansheet-Workspace { height: 100%; }

		/* Shared page container. Every standard content view roots on the ps-page class so the page inset and
		   centered max width live in one place instead of being redeclared per view. The -narrow and
		   -wide modifiers pick the reading-column and wide-table widths; -bleed is the full-width inset
		   for surfaces that fill the workspace (the kanban lanes, the settings pane). Genuinely bespoke
		   surfaces (the workflow canvas, the profile hero, the documentation browser) keep their own roots. */
		.ps-page { max-width: 1000px; margin: 0 auto; padding: 24px; box-sizing: border-box; }
		.ps-page-narrow { max-width: 860px; }
		.ps-page-wide { max-width: 1100px; }
		.ps-page-bleed { padding: 24px; box-sizing: border-box; }

		/* The docked navigation (pict-section-navigation) renders into #Plansheet-Sidebar, the left
		   panel's content-inner div. Pin it to the panel height with its own vertical scroll so a long
		   nav scrolls inside the rail. */
		#Plansheet-Sidebar { height: 100%; overflow: hidden; }

		/* Sidebar collapse tab: a tall, vertically centered handle that stays out of the way
		   (autohidden) until the pointer reaches the sidebar's edge, then slides out. This replaces
		   the module's short tab anchored near the top. Scoped to the left (sidebar) panel and under
		   the shell host so it wins over the module's own collapse-tab rules regardless of CSS
		   injection order. The :not(...collapsed) guard is important: when the sidebar is collapsed
		   the module turns the whole panel into the reopen strip (with !important), so the autohide
		   must not apply there or the collapsed sidebar would have no visible way back open. */
		.pict-modal-shell-host .pict-modal-shell-panel-left:not(.pict-modal-shell-panel-collapsed) > .pict-modal-shell-panel-collapse-tab
		{
			top: 50%;
			transform: translateY(-50%);
			right: -16px;
			width: 16px;
			height: 84px;
			opacity: 0;
		}
		.pict-modal-shell-host .pict-modal-shell-panel-left:not(.pict-modal-shell-panel-collapsed):hover > .pict-modal-shell-panel-collapse-tab,
		.pict-modal-shell-host .pict-modal-shell-panel-left:not(.pict-modal-shell-panel-collapsed) > .pict-modal-shell-panel-collapse-tab:hover
		{
			opacity: 1;
		}

		/* Sticker gallery: square the sticker tiles. The module sizes each thumbnail to a fixed
		   82px height inside a flexing-width cell, so the icons sit in a wide box. Pin the image to
		   a 1:1 box instead. !important wins over the module's injected rule regardless of CSS
		   injection order or where the (position: fixed) gallery is mounted. */
		.mb-gallery-mode-sticker .mb-gallery-item img { height: auto !important; aspect-ratio: 1 / 1 !important; }

		/* Print: shed the app chrome so any page -- above all a Blueprint opened in view mode --
		   prints as a clean sheet. The blueprint content renderer already carries its own @media
		   print rules; what was missing is the shell dropping its own furniture. Hide the nav rail,
		   help drawer, top bar and every collapse/resize/overlay element, then release the shell's
		   fixed height + overflow clipping (and switch its flex rows to block flow) so the content
		   runs down the page and across sheets instead of being cropped to one screen. */
		@media print {
			html, body, #App-Container,
			.pict-modal-shell-host, .pict-modal-shell,
			.pict-modal-shell-row-middle, .pict-modal-shell-center,
			.pict-modal-shell-center-content, #Plansheet-Workspace {
				height: auto !important; min-height: 0 !important; max-height: none !important;
				overflow: visible !important;
			}
			.pict-modal-shell, .pict-modal-shell-row-middle { display: block !important; }
			.pict-modal-shell-side-left, .pict-modal-shell-side-right,
			.pict-modal-shell-row-top, .pict-modal-shell-panel-top, .pict-modal-shell-row-bottom,
			.pict-modal-shell-panel-collapse-tab, .pict-modal-shell-panel-resize-handle,
			.pict-modal-shell-overlay-layer, #Plansheet-DrawingOverlay {
				display: none !important;
			}
			html, body, .pict-modal-shell-center { background: #ffffff !important; }
		}
	`,
	Templates: [{
		Hash: 'Plansheet-Shell',
		Template: /*html*/`<div id="Plansheet-Shell-Mount" style="height:100%"></div><div id="Plansheet-DrawingOverlay"></div>`
	}],
	Renderables: [{
		RenderableHash: 'Plansheet-Shell',
		TemplateHash: 'Plansheet-Shell',
		ContentDestinationAddress: '#App-Container'
	}]
};
